<template>
  <div ref="segmentControlRef" class="segment-control relative rounded-xl p-1">
    <Transition name="fade" :duration="150">
      <span
        v-if="activeId && pointerPosition"
        class="bg-background-neutral-secondary absolute rounded-lg shadow-md transition-all duration-300 ease-in"
        :style="pointerStyle"
      ></span>
    </Transition>
    <div class="relative grid" :class="gridClass || 'grid-flow-col-dense'">
      <slot />
    </div>
  </div>
</template>

<script lang="ts" setup>
  import { ref, provide, computed, onMounted, onBeforeUnmount } from 'vue';

  const props = defineProps<{
    activeId: string;
    gridClass?: string;
  }>();

  provide('updatePointerPosition', updatePointerPosition);
  provide('isActive', isActive);
  provide('getActiveSegmentRef', getActiveSegmentRef);

  const segmentControlRef = ref<HTMLDivElement>();
  const activeSegmentRef = ref<HTMLElement | null>(null);
  const pointerPosition = ref<{
    left: number;
    width: number;
    height: number;
  }>();

  let resizeObserver: ResizeObserver | null = null;

  const CONTAINER_PADDING = 4;

  const pointerStyle = computed(() => {
    if (!pointerPosition.value) return {};
    return {
      left: `${pointerPosition.value.left}px`,
      top: `${CONTAINER_PADDING}px`,
      width: `${pointerPosition.value.width}px`,
      height: `${pointerPosition.value.height}px`,
    };
  });

  function isActive(id: string) {
    return props.activeId === id;
  }

  function getActiveSegmentRef() {
    return activeSegmentRef;
  }

  function updatePointerPosition(
    boundingElRect: DOMRect,
    element?: HTMLElement,
  ) {
    if (!segmentControlRef.value) return;
    const containerRect = segmentControlRef.value.getBoundingClientRect();
    pointerPosition.value = {
      left: boundingElRect.left - containerRect.left,
      width: boundingElRect.width,
      height: boundingElRect.height,
    };
    if (element) {
      activeSegmentRef.value = element;
    }
  }

  function recalculatePosition() {
    requestAnimationFrame(() => {
      if (activeSegmentRef.value && segmentControlRef.value) {
        const boundingElRect = activeSegmentRef.value.getBoundingClientRect();
        const containerRect = segmentControlRef.value.getBoundingClientRect();
        pointerPosition.value = {
          left: boundingElRect.left - containerRect.left,
          width: boundingElRect.width,
          height: boundingElRect.height,
        };
      }
    });
  }

  onMounted(() => {
    if (segmentControlRef.value) {
      resizeObserver = new ResizeObserver(() => {
        recalculatePosition();
      });
      resizeObserver.observe(segmentControlRef.value);

      const mutationObserver = new MutationObserver(() => {
        recalculatePosition();
      });
      mutationObserver.observe(segmentControlRef.value, {
        childList: true,
        subtree: true,
      });
    }
  });

  onBeforeUnmount(() => {
    resizeObserver?.disconnect();
  });
</script>

<style scoped>
  .segment-control {
    background-color: var(
      --segment-conrol-background,
      rgb(var(--situational-lightgrey))
    );
  }
</style>
